/**
 * Top Navigation Component
 *
 * Horizontal navigation bar for all full-screen pages in the Rooke demo.
 * Provides consistent navigation between Approach, How It Works, Memory Graph,
 * and Rooke business unit views (Holdco, Panago, Phantom Screens, Apex Food).
 */

import { Link, useLocation } from 'react-router-dom';
import { Group, Text } from '@mantine/core';
import {
  IconInfoCircle,
  IconGitCompare,
  IconGraph,
  IconBuildingSkyscraper,
  IconPizza,
  IconWindow,
  IconLeaf,
  IconBrain,
} from '@tabler/icons-react';
import aldcLogo from '../assets/analytic-labs-logo.svg';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>;
}

const navItems: NavItem[] = [
  { path: '/approach', label: 'Approach', icon: IconGitCompare },
  { path: '/how-it-works', label: 'How It Works', icon: IconInfoCircle },
  { path: '/holdco', label: 'Kenbri Group', icon: IconBuildingSkyscraper },
  { path: '/panago', label: 'Panago', icon: IconPizza },
  { path: '/phantom-screens', label: 'Phantom Screens', icon: IconWindow },
  { path: '/apex-food', label: 'Apex Food', icon: IconLeaf },
  { path: '/daa', label: 'Decision Audit', icon: IconBrain },
  { path: '/memory-graph', label: 'Memory Graph', icon: IconGraph },
];

interface TopNavProps {
  title?: string;
}

export default function TopNav({ title = 'Kenbri Group' }: TopNavProps) {
  const location = useLocation();

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        justifyContent: 'space-between',
        height: 60,
      }}
    >
      {/* Left side - Logo and Title */}
      <Group gap="sm">
        <img
          src={aldcLogo}
          alt="Analytic Labs"
          style={{
            height: 32,
            width: 'auto',
          }}
        />
        <Text
          size="md"
          fw={600}
          c="white"
          style={{ lineHeight: 1, alignSelf: 'center' }}
        >
          {title}
        </Text>
      </Group>

      {/* Right side - Navigation Items */}
      <Group gap={4}>
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path === '/approach' && location.pathname === '/');
          const IconComponent = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: isActive
                    ? 'rgba(99, 102, 241, 0.9)'
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <IconComponent
                  size={16}
                  style={{ color: isActive ? '#fff' : '#94a3b8' }}
                />
                <Text
                  size="sm"
                  fw={isActive ? 500 : 400}
                  style={{ color: isActive ? '#fff' : '#94a3b8' }}
                >
                  {item.label}
                </Text>
              </div>
            </Link>
          );
        })}
      </Group>
    </div>
  );
}
